import { create } from 'zustand';
import { UAParser } from 'ua-parser-js';
import { defineSyncedStore, type SyncedStoreDef } from './yjs-provider';
import type { DeviceInfo, DeviceProfile } from '~types/device';

/**
 * Replication declaration (aggregated by src/store/registry.ts).
 * merge-defaults + scopedDiff: the registry self-heals — registerCurrentDevice
 * runs every boot.
 */
export const DEVICES_STORE_DEF: SyncedStoreDef<'devices'> = {
    name: 'devices',
    syncedKeys: ['devices'],
    hydration: 'merge-defaults',
    scopedDiff: true,
};
import packageJson from '../../package.json';

/**
 * Store for managing known devices in the sync mesh.
 * Wrapped with yjs() middleware for automatic CRDT synchronization.
 */
interface DeviceState {
    // === SYNCED STATE (persisted to Yjs) ===
    /** Map of device IDs to device info objects */
    devices: Record<string, DeviceInfo>;

    // === ACTIONS ===
    /**
     * Registers or updates the current device with full metadata and profile.
     */
    registerCurrentDevice: (deviceId: string, profile: DeviceProfile, name?: string) => void;

    /**
     * Updates the last active timestamp for a device.
     * Throttled to avoid excessive CRDT updates (default: 5 mins).
     */
    touchDevice: (deviceId: string) => void;

    /**
     * Updates the user-friendly name of a device.
     */
    renameDevice: (deviceId: string, name: string) => void;

    /**
     * Publishes THIS device's rolling daily GenAI spend onto its own synced
     * record (plan/semantic-search-design.md §3.4) for the project-wide
     * cross-device quota sum. Writes ONLY the given device's record via an
     * immutable spread — never clobbers another device's entry.
     *
     * COALESCED (see {@link EMBED_SPEND_RPD_STEP}): the QuotaGovernor calls
     * this on every acquire AND every commit of an embedding request, and each
     * accepted call is a CRDT write (Y.Doc transaction → y-idb row → outbound
     * push) plus a re-render of every subscriber of `devices`. The value is a
     * COARSE cross-device quota signal, so a same-day move smaller than the
     * step is dropped; a day rollover always writes.
     */
    publishEmbedSpend: (deviceId: string, spend: { day: string; rpd: number; tpm?: number }) => void;

    /**
     * Removes a device from the sync mesh.
     */
    deleteDevice: (deviceId: string) => void;
}

const HEARTBEAT_THROTTLE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Minimum same-day `rpd` movement that earns a synced write in
 * `publishEmbedSpend`. Readers (makeBackgroundQuotaLimits in
 * app/google/wireGoogle.ts, useQuotaMeters) sum this across OTHER devices to
 * divide a shared daily ceiling of ~1000 requests, so being up to 9 requests
 * stale per sibling is within the noise of a signal that is already a
 * microtask-batched CRDT replication away from live.
 */
const EMBED_SPEND_RPD_STEP = 10;

export const useDeviceStore = create<DeviceState>()(
    defineSyncedStore(
        DEVICES_STORE_DEF,
        (set) => ({
            devices: {},

            registerCurrentDevice: (deviceId, profile, name) => {
                set((state) => {
                    const existing = state.devices[deviceId];
                    const now = Date.now();

                    // Parse UA if not already fully registered or update if needed
                    // We always update to capture browser upgrades etc.
                    const parser = new UAParser();
                    const result = parser.getResult();

                    // Smart Name Generation (only if new)
                    let deviceName = existing?.name;
                    if (name) {
                        deviceName = name;
                    } else if (!deviceName) {
                        const browser = result.browser.name || 'Browser';
                        const os = result.os.name || 'Unknown OS';
                        const device = result.device.model ? ` ${result.device.model}` : '';
                        deviceName = `${browser} on ${os}${device}`;
                    }

                    return {
                        devices: {
                            ...state.devices,
                            [deviceId]: {
                                id: deviceId,
                                name: deviceName,
                                platform: result.os.name || 'Unknown',
                                browser: result.browser.name || 'Unknown',
                                model: result.device.model || null,
                                userAgent: result.ua,
                                appVersion: packageJson.version,
                                created: existing ? existing.created : now,
                                lastActive: now,
                                profile,
                                // Carry the device's own embedSpend forward:
                                // registerCurrentDevice runs on EVERY boot
                                // (deviceRegistration.ts) and rebuilds from a
                                // fresh literal — without this it would DELETE
                                // this device's own published spend each boot
                                // (the §3.4 self-clobber).
                                embedSpend: existing?.embedSpend
                            }
                        }
                    };
                });
            },

            touchDevice: (deviceId) => {
                set((state) => {
                    const existing = state.devices[deviceId];
                    if (!existing) return state;

                    const now = Date.now();
                    if (now - existing.lastActive < HEARTBEAT_THROTTLE_MS) {
                        return state; // Throttle
                    }

                    return {
                        devices: {
                            ...state.devices,
                            [deviceId]: {
                                ...existing,
                                lastActive: now
                            }
                        }
                    };
                });
            },

            renameDevice: (deviceId, name) =>
                set((state) => {
                    const existing = state.devices[deviceId];
                    if (!existing) return state;
                    return {
                        devices: {
                            ...state.devices,
                            [deviceId]: {
                                ...existing,
                                name
                            }
                        }
                    };
                }),

            publishEmbedSpend: (deviceId, spend) =>
                set((state) => {
                    const existing = state.devices[deviceId];
                    if (!existing) return state;
                    const previous = existing.embedSpend;
                    // Same day and a sub-step move: keep the stored (coarse)
                    // figure and the state object, so no CRDT update and no
                    // subscriber re-render. A new day ALWAYS writes — the
                    // reconciler drops spend stamped with a prior PT day, so a
                    // stale stamp would silently stop counting this device.
                    if (
                        previous !== undefined &&
                        previous.day === spend.day &&
                        Math.abs(spend.rpd - previous.rpd) < EMBED_SPEND_RPD_STEP
                    ) {
                        return state;
                    }
                    return {
                        devices: {
                            ...state.devices,
                            [deviceId]: {
                                ...existing,
                                embedSpend: spend
                            }
                        }
                    };
                }),

            deleteDevice: (deviceId) =>
                set((state) => {
                    const { [deviceId]: _removed, ...rest } = state.devices;
                    return { devices: rest };
                }),
        })
    )
);
